// Type-only, so the reverse dependency application-parties.domain already has on this file stays a
// compile-time cycle that erases rather than a runtime import cycle.
import type { ApplicationPartiesWrite, GetApplicationPartiesPayload } from './application-parties.domain';
import {
  ApiResponse,
  AzureStorageDocument,
  Base64File,
  DocumentTag,
  InteflowStatusRecord, NotNullable, OriginatorBusiness,
  PaymentPeriodType,
  RiskRatingInterface,
  SortTypes,
  User,
  UserOmitPriviledges,
} from './types.domain';
import {EncodingTypes} from './types.domain';
import {AssetFinanceTier1, AssetFinanceTier2, AssetFinanceTier3, AssetFinanceTier4} from './admin-service.domain';
import { UserSelectionValue } from './component/user-selection-component.domain';
import {IndividualWithResult, ThirdPartyEntity, UpdateIndividualData} from './digital-id-service.domain';
import type {SaveApplicationPartyEntityInput} from './kyb-entity-summary.domain';
import {BusinessNumberSearchValue} from './component/business-number-search-component.domain';
import { PrivateSaleType, TransactionType } from './component/transaction-type-component.domain';
import {Observable} from 'rxjs';
import { PayloadApiResponse } from './types.domain';
import {Response} from 'request';
import {TruckGrossVehicleWeight} from './redbook-service.domain';
import {TransactionTypeRateValue} from './component/transaction-type-rate-component.domain';
import { CurrencyInputValue } from './component/currency-input-component.domain';
import { BsbComponentValue } from './component/bsb-component.domain';
import {DscrIndustry, DscrIndustryEbitda} from '../const';
import {LtvSelectionType} from "./component/ltv-selection-component.domain";
import {SecurityTypeSelectionType} from "./component/security-type-selection-component.domain";
import {SecurityTypeRateValue} from "./component/security-type-rate-component.domain";
import {SalesforceContactSameAccountSelectionValue} from "./component/salesforce-contact-same-account-selection-component.domain";
import { FinanceType } from './component/finance-type-component.domain';
import { IdentificationComponentValue } from './component/identification-component.domain';
import {IncomePeriodType} from "./component/income-component.domain";
import { RepaymentFrequencyType } from '../app-calculator';
import { CreditApprovedDetails, CreditDeclineDetails, CreditOfficerStatus, SystemCreditStatus } from './credit.domain';
import {
  KybKeyPeopleValue,
  KybPersonRowState,
  KybReconciliationSaveData,
  KybReconciliationSaveResult,
} from './kyb-key-people.domain';
import { Moment } from 'moment';
import { CustomerApplicantTypeValue } from './component/customer-applicant-component.domain';
import { CheckActiveOverdraftAccountDetails, PismoProgram, getPismoProgramsFn } from './pismo.domain';
import { SearchableSelectItem } from './selection.domain';
import { LoanPurposeValue } from './component/loan-purpose-selection-component.domain';
import { IndustryDivision,
  IndustryGroup,
  IndustryClass,
  GetIndustryDivisionsFn,
  GetIndustryGroupsByDivisionCodeFn,
  GetIndustryClassesByGroupCodeFn} from './industry.domain';

export type GetRbaRateFn = () => Observable<number>;

export type StageNameType = 'Application Received' | 'Closed Lost' | 'Closed Won' | 'Credit Approved'  | 'Docs Out' | 'Documentation' | 'Draft' | 'Lead' | 'More Information' | 'Proposal' |
  'Prospect' | 'QA' | 'Settlement' | 'Settlement Pending' | 'Submitted to Credit' | 'Waiting for Bank Statements';

export const STAGE_NAMES_UNDER_REVIEW: StageNameType[] = ['Submitted to Credit', 'Waiting for Bank Statements', 'More Information'];
export const STAGE_NAMES_IN_SETTLEMENT: StageNameType[] = ['Docs Out', 'Settlement Pending', 'QA', 'Settlement'];
export const STAGE_NAMES_CLOSED_WON: StageNameType[] = ['Closed Won'];
export const STAGE_NAMES_APPROVED: StageNameType[] = ['Credit Approved', 'Documentation'];

export const STAGE_NAMES_ALL: StageNameType[] = [
  'Application Received', 'Closed Lost', 'Closed Won', 'Credit Approved', 'Docs Out', 'Documentation', 'Draft', 'Lead', 
  'More Information', 'Proposal', 'Prospect', 'QA', 'Settlement', 'Settlement Pending', 'Submitted to Credit', 'Waiting for Bank Statements'
];

/**
 * Stages at which the party structure (business entities and their trustees/partners/guarantors) is
 * frozen: once documents have been issued the parties named on them must not change. Docs Out and
 * everything after it. getApplicationStatus collapses these into 'Approved', so gate on the raw
 * stage name, not the derived status.
 */
export const STAGE_NAMES_PARTY_STRUCTURE_LOCKED: StageNameType[] = [
  'Docs Out', 'Documentation', 'Settlement Pending', 'Settlement', 'Closed Won', 'Closed Lost',
];

/** True when the application's party structure can no longer be changed post-submission. */
export const isPartyStructureLocked = (stageName: StageNameType | null | undefined): boolean =>
  !!stageName && STAGE_NAMES_PARTY_STRUCTURE_LOCKED.includes(stageName);

/**
 * Stages where the client is expected to supply bank statements. Uploading them anywhere else, after
 * credit approval in particular, must not offer to send the application back to Submitted to Credit.
 */
export const STAGE_NAMES_AWAITING_BANK_STATEMENTS: StageNameType[] = ['Waiting for Bank Statements', 'More Information'];

export const canSubmitBankStatementsForCreditReview = (stageName: string | null | undefined): boolean =>
  !!stageName && STAGE_NAMES_AWAITING_BANK_STATEMENTS.includes(stageName.trim() as StageNameType);

export interface LvrCalculatorInput { // input required to update Lvr Calculator Value
  assets: {description: string, value: number}[],
  liabilities: {description: string, value: number}[],
  proposedLoan: number, // this is application's PricingDetails's AmountFinanced
}

export interface LvrCalculatorValue extends LvrCalculatorInput { // Lvr Calculator value + computed values
  totalAssets: number,
  totalLiabilities: number,
  currentLvrPercent: number | null,
  proposedLvrPercent: number | null,
}

export interface DscrCalculatorInput {
  customerName: string,
  loanAmount: number, // this is application's Loan Amount
  industry: DscrIndustry | null,
  industryEbitda: DscrIndustryEbitda | null,
  adjustmentPercent: number,
  monthlySalesAverage3Months: number,
  monthlySalesAverage6Months: number,
  monthlySalesAverage12Months: number,
  averageMonthlySalesAdopted: number,
  salesAdjustment: number,
  averageMonthlySalesAdoptedAfterAdjustment: number,
  existingDebtCommitmentsMonthly: number,
  atoPaymentPlanMonthly: number,
  additionalCommitments: number,
  proposedCommitmentsMonthly: number,
}

export interface DscrCalculatorValue extends DscrCalculatorInput {
  marginAdoptedPercent: number,
  cashAvailableForDebtServiceMonthly: number,
  // proposedCommitmentsMonthly: number,
  totalFinancingCommitmentsMonthly: number,
  debtServiceCoverRatio: number,
  proposedLoanAmount: number,
}




export interface SaveQuoteResult {
  quoteId: number ;
}

export interface Quote {
  LoanAmount: string,
  RepaymentFrequency: string,
  LoanTerm: number,
  Residual?: number,
  BrokeragePercentage?: number,
  /** @deprecated Business Overdraft no longer captures Facility Establishment Fee.
   *  Field retained for legacy quote rows; new BO calculator submissions do not set it. */
  FacilityEstablishmentPercentage?: number,
  BrokerageAmount: number,
  QuoteAsJson: string,
}

export type ApplicationFilterType = 'all' | 'under-review' | 'in-settlement' | 'closed-won';

export type ApplicationSortTypes = SortTypes<'ApplicationId' | 'BrokerAppId' | 'CompanyName' | 'Status' | 'AppInfoStageName' | 'CreateTime' | 'BrokerName'>;

export type LocalApplicationDbReloadFn = (data: {
  page: {offset: number, limit: number},
  forceReload: boolean,
  sorts?: ApplicationSort,
  filter?: string,
  applicationStage?: StageNameType[],
  applicationType?: ApplicationTypes[],
  filterOpportunityOwner?: string[],
  filterCreditOfficer?: string[],
  filterSettlementOfficer?: string[],
  dateFilter:{startDate?: Date, endDate: Date},
  minDate?: Date,
  statistic?: string
}) => Observable<{
  totals: {
    underReview: number,
    inSettlement: number,
    closedWon: number,
    approved: number
  },
  opportunityOwners: { firstName?: string | null, lastName?: string | null, email?: string | null, count?: number}[],
  creditOfficers: { firstName?: string | null, lastName?: string | null, email?: string | null, count?: number}[],
  settlementOfficers: { firstName?: string | null, lastName?: string | null, email?: string | null, count?: number}[],
  applicationStages: { name?: string | null, count?: number}[],
  applicationTypes: { name?: string | null, count?: number}[],
  total: number,   // total applications search / sorts / filter
  limit: number,   // limit of applications search / sorts / filter
  offset: number,  // offset of applications search / sorts / filter
  applications: SimplifiedApplication[],
  graphData: {
    id: string;
    segmentStart: moment.Moment;
    segmentEnd: moment.Moment;
    label: string;
    underReview: number;
    inSettlement: number;
    closedWon: number;
    closedLost: number;
    approved: number;
    total: number;
    tooltip: string;
  }[]
}>;

export interface SimplifiedApplication {
  ApplicationId: number,
  Source: ApplicationSource,
  BrokerAppId: string,
  ApplicationType: ApplicationTypes,
  Status: string,
  CreateTime: string,
  CommercialEntityLegalName: string | null,
  IndividualGivenName: string | null,
  IndividualSurName: string | null,
  SalesforceId: string | null,
  BrokerSalesforceID: string | null,
  StageName: string,
  CompanyName: string,
  BrokerName: string,
  PricingDetails: { LoanAmount: number },
  opportunityOwnerEmail?: string | null,
  opportunityOwnerFirstName?: string | null,
  opportunityOwnerLastName?: string | null,
  creditOfficerEmail?: string | null,
  creditOfficerFirstName?: string | null,
  creditOfficerLastName?: string | null,
  settlmentUserEmail?: string | null,
  settlmentUserFirstName?: string | null,
  settlmentUserLastName?: string | null,
  // InteflowStatus: any
}

export interface RatecardDetails {
  ABNorGSTAge: {To?: string, From?: string, Rate: string}[];
  AdversOnFile: number;
  ApplicationType: 'AssetFinance' | 'TradeFinance' | 'InsurancePremium' | 'BusinessLoans' | 'BusinessOverdraft' | 'Consumer' | 'CorporateLoans';
  ApplicationCategory: {Cat: string, Rate: number}[];
  AssetConditionUsed: number;
  AssetFinanceTier1: AssetFinanceTier1;
  AssetFinanceTier2: AssetFinanceTier2;
  AssetFinanceTier3: AssetFinanceTier3;
  AssetFinanceTier4: AssetFinanceTier4;
  AssetType: {Cat: string, Type: string, Rate: number}[];
  AssetCategory: {Cat: string, Rate: number}[];
  BaseInterestRate: number;
  Brokerage: {To?: string, From?: string, Rate: string}[];
  CarsPropertyOwner: 0 | 1;
  CreateTime: string;
  DocFee: {From?: string, To?: string, Rate: string}[];
  EOTAge: {From?: string, To?: string, Rate: string}[];
  GSTAge24Months: number,
  LoanAmount: {From?: string, To?: string, Rate: string}[];
  LoanTerms: {From?: string, To?: string, Rate: string}[];
  LowDirectorScoreUplift: number;
  LowDepositUplift: number;
  // AZ-1651: percent taken off the Asset Finance interest rate when GST age, the
  // 700 director-score toggle and property ownership all qualify. Null is 0.
  PremiumDiscount?: number | null;
  Name: string;
  NoPropertyOwnership: 0 | 1;
  CapRate: number;
  Truck12T: number;
  NoAssetBacked: number;
  TransactionTypeRate: TransactionTypeRateValue;
  PrivateSale: number;
  PrivateSaleDocFee: number;
  RateDetailId: number;
  RatecardId: number;
  UpdateTime: string;
  PropertyOwner:number;
  NoPropertyOwner:number;
  // ZEN-818: new additive uplift model for Business Term Loan / Business Overdraft.
  // PropertyOwner is the base rate (property owner + good credit score). Legacy
  // matrix columns (NoPropertyOwner, DirectorScoreRate, NonPropertyOwnerBetween500And600Rate)
  // are retained for rollback but no longer consulted by the calculator.
  NoPropertyOwnerUplift: number;
  LowScoreUplift: number;
  FacilityFee: number;
  BrokerShareFacilityFee: number;
  DirectorScoreRate: number;
  Hybrid: number;
  CarsAndCaravans: {From?: string, To?: string, Rate: string}[];
  Motorbikes: {From?: string, To?: string, Rate: string}[];
  SolarSystems: {From?: string, To?: string, Rate: string}[];
  NonPropertyOwnerBetween500And600Rate: number;
  rbaRate: number;
  SecurityType?: SecurityTypeRateValue;
  MonthlyAccountKeepingFee: number;
  MaxBrokerRateDiscount: number;
  CreditAssitanceFee: number;
  PpsrFee: number;
  MaxFacilityEstablishmentFee: number;
  // ZEN-818 follow-up: 3-tier pricing for BusinessLoans / BusinessOverdraft.
  // Populated only for BL/OD rate cards. Corporate Loan keeps using the uplift columns above.
  Tiers?: { tier1: number; tier2: number; tier3: number } | null;
}

// ZEN-818 follow-up: threshold string written to PricingDetails.EquifaxDirectorScoreThreshold.
// Each product's UI toggle writes a product-specific threshold:
//   - BL/OD:       '>=700' / '<700'
//   - Asset Fin:   '>=700' / '>=550' / '<550'
//                  ('>=700' is Equifax Director Score above 700 and implies the 550 toggle is Yes.
//                   thresholdMeets treats a stored '>=700' as meeting '>=550'.)
//   - Corporate:   '>=600' / '<600'
//   - Consumer:    '>=600' / '<600'
export type EquifaxDirectorScoreThresholdValue =
  | '>=500' | '>=550' | '>=600' | '>=700'
  | '<500' | '<550' | '<600' | '<700'
  | null;

export interface BankStatement {
  filename: string;
  id: string;
}

export type RoleTypes = 'Applicant' | 'Director' | 'Guarantor' | 'Partner' | 'Shareholder' | 'SoleTrader' | 'Trustee' | 'Spouse' | null | string;
export type SignerRoleTypes = 'Signer' | 'Guarantor' | 'GuarantorSigner' | 'Others' | 'CorporateGuarantor' | null;

export interface AppDocument {
  name: string, type: string, salesforceFileId?: string, sharepointId?: string, isPPSRDoc?: boolean,
  data: string,
  encoding?: EncodingTypes,
}

export interface DocumentResult {
  name: string,
  result:  {id: string, success: boolean, errors: string[]},
  salesforceFileId?: string,
}

export interface Reference {
  CompanyName: string,
  ContactPerson: string,
  Telephone: string,
  Type: string,
}

export interface Contact {
  Email: string,
  GivenName: string,
  MiddleName: string,
  Mobile: string,
  SurName: string,
  Title: string,
  Telephone: string,
  AreaCode: string,
  isManual: boolean,
  Contact:string
}

export type UnformattedAddress = Address;
export interface Address {
  UnitNumber?: string,
  Suburb: string,
  State: string,
  Postcode: string,
  StreetName: string,
  StreetNumber: string,
  UnformattedAddress: string,
  IsManualInput: boolean,
  StreetType: string,
}

export type RawAddress = Address2;
export interface Address2 {
  UnitNumber?: string,
  Suburb: string,
  State: string,
  Postcode: string,
  StreetName: string,
  StreetNumber: string,
  RawAddress: string,
  IsManualInput: boolean,
  StreetType: string,
}

export interface Individual {
  AddressofPropertyOwned?: Address,
  DoB: string,
  Email: string,
  Gender: string,
  GivenName: string,
  GuarantorFlag: boolean,
  HomeAddress: Address,
  MiddleName: string,
  MobileNumber: string,
  HasAlias?: boolean,
  AliasNames?: string,
  CountryOfCitizenship?: string,
  IdentificationType?: string,
  IdentificationNumber?: string,
  deleted?: boolean;  // true means deleted, else not deleted
  disabled?: boolean;
  MortgageBalance?: number,
  PropertyOwnerFlag: boolean,
  PropertyValue?: number,
  Role: RoleTypes,
  SignerRole: SignerRoleTypes,
  SurName: string,
  Title: string,
  id?: string,
  sfContactId?: string,
  thirdPartyEntity?: ThirdPartyEntity,
  additionalRoles?: IndividualAdditionalRole[],
  isNameOrDobChanged?: boolean;
  Contact: string;
  // Read-only projection of ApplicationIndividual.source: which save owns the row. Set on read so a
  // reader can tell a party a wizard step captured from one the KYB save materialised; the save
  // payloads never carry it back. See isKybSourcedParty.
  source?: string | null;
  // Read-only projections, like source, of the columns behind the individuals table's needs-attention
  // flag and its refusal to expand a row whose form has never been saved.
  needsAttention?: boolean;
  detailsSaved?: boolean;
  // Read-only projection of ApplicationIndividual.legacyPrivacyConsent (pre-5.9.0 broker checkbox).
  // Not written back; AZ-1629 credit-flow grandfather treats a true flag as consent.
  legacyPrivacyConsent?: boolean | null;
  // ID documents (driver licence / passport) recorded against this individual, so a reader can tell
  // whether one is on file without listing the application's blob storage.
  idDocuments?: IndividualIdDocument[];
}

/**
 * An ID document filed against an individual. `name` is the Azure blob name, which is what the
 * documents listing and the download/delete calls key on.
 */
export interface IndividualIdDocument {
  name: string;
  fileName: string;
  documentType: string;
  uploadedAt: string;
}

export interface IndividualAdditionalRole {
  business: BusinessNumberSearchValue,
  signerRole: SignerRoleTypes,
}

export interface ConsumerIndividual extends Individual {
  Employer: string,
  EmployerContact: string,
  EmploymentStatus: string, // EmploymentStatus.name
  Income: number,
  IncomeFrequency: IncomePeriodType,  // IncomePeriodType
  MaritalStatus: string,  // MaritialStatus.name
  NumberofDependants: number,
  TimeatAddress: number,
  PreviousAddress?: Address,
  TimeatCurrentEmployment: number,
  PreviousEmployer?: string,
  PreviousEmployerContact?: string,
  Identification?: IdentificationComponentValue
}
export interface IndividualPropertyAsset {
  AssetType: string, Value: string, Address: Address
}
export interface IndividualOtherAsset {
  AssetType: string, Value: string, Description: string
}
export interface IndividualMortgageLiability {
  Type: string, Value: string, Address: Address,
}
export interface IndividualOtherLiability {
  Type: string, Value: string, Financier: string
}
export interface IndividualWithAssetsAndLiabilities extends Individual {
  Assets?: (IndividualPropertyAsset | IndividualOtherAsset)[],
  Liabilities?: (IndividualMortgageLiability | IndividualOtherLiability)[],
}

export interface CommercialEntity {
  ABN: string,
  ACN: string,
  AreaCode: string,
  BusinessCategory: string,
  EntityType: 'P/L' | 'PTNR' | 'PTY' | 'SLTR' | 'TRST' | null,
  IndustrySector: string,
  IndustryClass: string,
  IndustryDivision?:IndustryDivision,
  IndustryGroup?:IndustryGroup,
  IndustryClassDetail?:IndustryClass
  LegalName: string,
  OperateatCommercialAddressFlag: boolean,
  PhoneNumber: string,
  PrimaryIndustry: string,
  PrinciplePlaceofBusiness: Address,
  // Registered office per the registry, prefilled from KYB on the company details step. Held
  // locally only - deliberately not part of the Salesforce or Inteflow field mappings.
  RegisteredBusinessAddress?: Address,
  Revenue: number,
  TradingName: string,
  manualEntry: boolean,
  Type: string,
  ABNAgeMonths: number
}


export interface AssetSpec {

  make: string,
  family: string,
  year: number | string,
  vehicle: string,
  LVR: string | null,
  description: string,
  avgretail?: number,

  goodretail?: number,
  newPrice?: number,
  OtherCar: boolean,
  truckGrossVehicleWeight?: TruckGrossVehicleWeight,
  descriptionSf?: string,
  vehicletype?:string,
  rbid?:number,
  rbc?:number
  newprice?:number,
  month?:number,
  makecode?:string,
  importflag?:string,
  goodwhs?: number,
  avgwhs?: number,
  bodystyle?: string,
}


export type ApplicationTypes = 'AssetFinance' | 'BusinessLoans' | 'TradeFinance' | 'Consumer' | 'InsurancePremium' | 'Commercial' | 'BusinessOverdraft' | 'CorporateLoans';
export type CustomContentLoaderType = 'facebook' | 'list' | 'bullet' | 'custom';
export type AllApplicationTypes = 'All' | ApplicationTypes;
export type PublishedStatus = 'All' | true | false;
export type CreditFlowTypes = ApplicationTypes | 'AssetLowDoc' | 'AssetEasyDoc' | 'AssetFullDoc';

export type AssetFinanceProductType = 'FAST DOC' | 'EASY DOC' | 'FULL DOC';

export interface AssociatedEntity {
  abn: string;
  acn: string;
  name: string;
}

interface DefaultApplication {
  /**
   * The parties in the redesigned shape, attached by attachApplicationParties from the same snapshot
   * it builds the legacy arrays from — so it costs no extra query and is present exactly wherever
   * those arrays are. Identical to what POST /api2/get-application-parties returns, being built by
   * the same two projections, so a component can move between the endpoint and this field freely.
   *
   * Optional because an Application assembled without going through that hydrator will not have it.
   *
   * This is the replacement for Individuals/CommercialEntities on the read path (AZ-1475). Not a
   * column, and not on the write types: SaveApplicationData.parties is a different, narrower shape
   * for saving.
   */
  parties?: GetApplicationPartiesPayload;
  isDeleted: boolean;
  Source: ApplicationSource;
  GrowRiskRatingData?: RiskRatingInterface | null,
  AppInfoSalesforceID?:string,
  AppInfoBrokerSalesforceID?: string,
  AppInfoStageName?: StageNameType | null,
  BrokerName: string,
  BrokerSalesforceId: string,
  BrokerABN: string,
  BrokerACN: string,
  AcceptQuotation: boolean,
  OppStatus: InteflowStatusRecord,
  opportunityOwnerId: number | null,
  opportunityOwnerEmail: string |null,
  opportunityOwnerFirstName: string |null,
  opportunityOwnerLastName: string |null,
  creditOfficerId?: number,
  creditOfficerEmail: string | null,
  creditOfficerFirstName: string | null,
  creditOfficerLastName: string | null,
  settlmentUserId: number | null,
  settlmentUserEmail: string | null,
  settlmentUserFirstName: string | null,
  settlmentUserLastName: string | null,
  /** Portal-only, not synced to Salesforce. */
  amlOfficerUserId: number | null,
  amlOfficerFirstName: string | null,
  amlOfficerLastName: string | null,
  amlOfficerEmail: string | null,
  AdditionalBrokerCorrespondent: UserSelectionValue,
  BrokerContact: SalesforceContactSameAccountSelectionValue,
  ApplicationId: number,
  ApplicationNotes: string,
  BrokerAppId: string,
  OriginatorBusinessId: number | null,
  CompanyName: string,
  CreateTime: string,
  DocumentResult?: DocumentResult[],
  //Documents?: AppDocument[] | Base64File[], // any[],//AzureStorageDocument[],  // TODO: find out why this is type to any[], shouldn't be that way.
  IP: string,
  InteflowStatus: {
    StageName: StageNameType,
    ec1_CreditReasoning__c: string,
    ec1_Credit_Approval_DateTime__c: string,
    ec1_Credit_Approval_Date__c: string,
  } | null,
  InteflowStatusStageName: InteflowStatusStageType,
  QuoteId: null,
  Reference: Reference[],
  Status: string,
  UserId: number,
  entityName: string,
  DocumentTypes?: DocumentTag[],
  DirectSalesUserFirstName?: string;
  DirectSalesUserLastName?: string;
  DirectSalesUserEmail?: string;
  SystemCreditStatus?: SystemCreditStatus,
  CreditOfficerStatus?: CreditOfficerStatus,
  CreditApprovedDetails?: CreditApprovedDetails,
  CreditDeclineDetails?: CreditDeclineDetails,
  RunCreditFlow: boolean,
  SequenceNumber: number,
  ExposureAtSubmission?: number | null,
  AssociatedEntities?: AssociatedEntity[],
  // When true, the borrowing (primary) entity is a property owner and is added as a guarantor on
  // the generated contract (every signer signs on behalf of that entity). Contract-only in effect.
  isBorrowerPropertyOwner?: boolean | null,
  // Set once every active individual has passed KYC and the primary borrower entity has passed
  // KYB. Mirrored to the Opportunity as FrankieOne_Completed__c by cronSubmitApplication.
  FrankieOneCompleted?: boolean | null,
  // True when every non-deleted guarantor, or failing that every non-deleted individual, has a
  // signed DocuSign privacy form. The pre-5.9.0
  // broker checkbox does not count. Mirrored to Docusign_Privacy_Completed__c by cronSubmitApplication.
  DocusignPrivacyCompleted?: boolean | null,
  // Base contract number without change suffixes (Pismo account id for overdrafts). Renewals /
  // limit changes share the same ContractNumber and increment ContractVersion (1 = original).
  ContractNumber?: string | null,
  ContractVersion?: number | null,
}

interface DefaultAppInfo {
  StageName: StageNameType | null;
  SalesforceId?: string | null,   // OppId in salesforce
  CustomerId?: string | null,     // AccountId in salesforce
  ContactId?: string | null,
  BrokerAppID: string,
  BrokerSalesforceID: string,
  BrokerAbn?: string,
  BrokerEntityName?: string,
  FinanceType: string,
  IntroducerGroup: string,
  Notes: string,
  PrivacyConfirmation?: 'Yes' | 'No',
  SelfServiceability: string,
  UserEmail: string,
  UserFirstName: string,
  UserLastName: string,
  /** @deprecated no longer collected: the "have you met the applicant" question was removed with the unified individual form; kept for historical AppInfo values */
  MetApplicant?: NotNullable<CustomerApplicantTypeValue>['type'],
  LoanPurpose?: LoanPurposeValue | null,
}

interface AssetFinanceAppInfo extends DefaultAppInfo {
  AssetCategory: string,
  AssetType: string,
  BrokerflowDocumentID?: string,
  BusinessUse?: 'Yes' | 'No',
  isSwitchToEasyDoc: boolean,
  productType: AssetFinanceProductType,
  LoanPurpose: LoanPurposeValue | null;
}
interface CommercialFinanceAppInfo extends DefaultAppInfo {
  AssetCategory: string,
  AssetType: string,
  BrokerflowDocumentID?: string,
  BusinessUse?: 'Yes' | 'No',
}
interface BusinessLoansAppInfo extends DefaultAppInfo {
  AssetCategory: string,
  AssetType: string,
  BrokerflowDocumentID?: string,
  BusinessUse?: 'Yes' | 'No',
  LoanPurpose: LoanPurposeValue | null;
}
interface TradeFinanceAppInfo extends DefaultAppInfo {
  BusinessDescription: string,
  BusinessSell: null,
  BusinessSellTerms: null,
  BusinessUse?: 'Yes' | 'No',
}
interface CorporateLoanAppInfo extends DefaultAppInfo {
  BrokerflowDocumentID?: string,
  BusinessDescription: string,
  BusinessSell: null,
  BusinessSellTerms: null,
  BusinessUse?: 'Yes' | 'No',
}
interface ConsumerFinanceAppInfo extends DefaultAppInfo {
  AssetCategory: string,
  AssetType: string,
  LoanPurpose: LoanPurposeValue | null;
}
interface InsurancePremiumAppInfo extends DefaultAppInfo {
  BusinessUse?: 'Yes' | 'No',

}
interface BusinessOverdraftAppInfo extends DefaultAppInfo {
  AssetCategory: string,
  AssetType: string,
  BrokerflowDocumentID?: string,
  BusinessUse?: 'Yes' | 'No',
  LoanPurpose: LoanPurposeValue | null;

}

export interface AssetFinanceApplication extends DefaultApplication {
  ApplicationType: 'AssetFinance',
  AppInfo: AssetFinanceAppInfo,
  ApplicationLocation: { lat: string, long: string},
  AssetSpec: AssetSpec,
  AuthorisedSignatory: string | null,
  CompanyDetails: SearchCompanyByABNResult,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: boolean,
  Policies: null,
  PricingDetails: {
    FacilityEstablishmentFee: number,
    FacilityEstablishmentFeePercent: number,
    AdverseOnFile: 'Yes' | 'No',
    AssetCondition: 'New' | 'Used',
    TreasuryAssetCondition?: 'New' | 'Used', // derived from asset age (<= 2yrs => New); synced to Treasury/SF
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    BrokerOriginationFee: number,
    Brokerage: number,
    Deposit: number,
    DocFee: number,
    DocFeeFinanced: string,
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    // AZ-1651: percent taken off this quote. 0 when the discount did not apply.
    PremiumDiscount?: number | null,
    InvoiceAmount: number,
    AmountFinance: number,
    LoanAmount: number,
    LoanTerm: number,
    PaymentPeriod: PaymentPeriodType | null,
    PrivateSale: PrivateSaleType,
    PropertyOwner: 'Yes' | 'No',
    Margin: number;
    Rate: number,
    CreditRateAdjustment?: number,
    Repayment: number,
    PaymentType: string,
    BrokerageAmount: number, // from SF
    BrokerOriginationFeeSf: number, // from SF
    DocFeeSf: number, // from SF
    RateSf: number, // from SF
    SpecialConditions?: string, // from SF,
    TransactionType?: TransactionType,
    PreviousLoan: 'Yes' | 'No',
    LastUpdatedByUser?: {id: number, name: string} | null,
    RepaymentType?: string
  },
  PreviousBusiness: BusinessNumberSearchValue;
}

export interface CommercialFinanceApplication extends DefaultApplication {
  ApplicationType: 'Commercial',
  AppInfo: CommercialFinanceAppInfo,
  ApplicationLocation: { lat: string, long: string},
  AssetSpec: AssetSpec,
  AuthorisedSignatory: string | null,
  CompanyDetails: null,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: boolean,
  Policies: null,
  PricingDetails: {
    AdverseOnFile: 'Yes' | 'No',
    AssetCondition: 'New' | 'Used',
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    BrokerOriginationFee: number,
    Brokerage: number,
    Deposit: number,
    DocFee: number,
    DocFeeFinanced: string,
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    InvoiceAmount: number,
    LoanAmount: number,
    LoanTerm: number,
    PaymentPeriod: PaymentPeriodType | null,
    PrivateSale: 'Yes' | 'No',
    PropertyOwner: 'Yes' | 'No',
    Margin: number;
    Rate: number,
    Repayment: number,
    PaymentType: string,
    BrokerageAmount: number,
    AbnAgeAboveThresholdOverride?: 'Yes' | 'No' | null,
  },
  PreviousBusiness: BusinessNumberSearchValue,
}

export interface BusinessLoansApplication extends DefaultApplication {
  ApplicationType: 'BusinessLoans',
  LvrCalculatorValue: LvrCalculatorValue,
  DscrCalculatorValue: DscrCalculatorValue,
  AppInfo: BusinessLoansAppInfo,
  ApplicantLocation: {lat: string, long: string},
  AssetSpec: null,
  AuthorisedSignatory: null,
  CompanyDetails: SearchCompanyByABNResult,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: false,
  Policies: null,
  PricingDetails: {
    FacilityEstablishmentFee: number,
    FacilityEstablishmentFeePercent: number,
    AdverseOnFile: 'Yes' | 'No',
    BankStatementSubmitted: string,
    BrokerOriginationFee: number,
    Brokerage: number,
    DocFee: number,
    DocFeeFinanced: string,
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    InvoiceAmount: number,
    LoanAmount: number,
    AmountFinance?: number,  // filled by SF pricing details callback
    LoanTerm: number,
    PaymentPeriod: PaymentPeriodType | null,
    PropertyOwner: 'Yes' | 'No',
    Margin: number;
    Rate: number,
    CreditRateAdjustment?: number,
    Repayment: number,
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    DirectorScoreRate: 'Yes' | 'No',
    PaymentType: string,
    BrokerageAmount: number, // from SF
    BrokerOriginationFeeSf: number, // from SF
    DocFeeSf: number, // from SF
    RateSf: number, // from SF
    SpecialConditions?: string // from SF,
    PreviousLoan: 'Yes' | 'No',
    LastUpdatedByUser?: {id: number, name: string} | null,
    RepaymentType?: string,
    AbnAgeAboveThresholdOverride?: 'Yes' | 'No' | null,
  },
  PreviousBusiness: BusinessNumberSearchValue;
}

export interface TradeFinanceApplication extends DefaultApplication {
  ApplicationType: 'TradeFinance',
  AppInfo: TradeFinanceAppInfo,
  ApplicantLocation: {lat: string, long: string},
  AssetSpec: null,
  AuthorisedSignatory: null,
  CompanyDetails: SearchCompanyByABNResult,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: boolean,
  Policies: null,
  PricingDetails: {
    LimitRequest: number,
    Deposit: number,
    Margin: number;
  },
  PreviousBusiness: BusinessNumberSearchValue;
}

export interface CorporateLoanApplication extends DefaultApplication {
  ApplicationType: 'CorporateLoans',
  AppInfo: CorporateLoanAppInfo,
  ApplicantLocation: { lat: string, long: string },
  AssetSpec: null,
  AuthorisedSignatory: null,
  CompanyDetails: SearchCompanyByABNResult,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: boolean,
  Policies: null,
  PricingDetails: {
    CreditRateAdjustment?: number,
    Rate: number,
    DocFee: number,
    BrokerageAmount: number,
    Brokerage: number,
    RbaRate: number,
    PreviousLoan: 'Yes' | 'No',
    DirectorScoreRate: 'Yes' | 'No',
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    AdverseOnFile: 'Yes' | 'No',
    PropertyOwner: 'Yes' | 'No',
    PaymentPeriod: PaymentPeriodType | null,
    LoanAmount: number,
    FacilityEstablishmentFee: number,
    FacilityEstablishmentFeePercent: number,
    LimitRequest: number,
    LoanTerm: number,
    Deposit: number,
    Margin: number;
    ltv: LtvSelectionType,
    securityType: SecurityTypeSelectionType,
    BankStatementSubmitted: string,
    BrokerOriginationFee: number,
    DocFeeFinanced: string,
    InvoiceAmount: number,
    AmountFinance?: number,  // filled by SF pricing details callback
    Repayment: number,
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    PaymentType: string,
    SpecialConditions?: string,
    LastUpdatedByUser?: {id: number, name: string} | null,
  },
  PreviousBusiness: BusinessNumberSearchValue;
}

export interface ConsumerFinanceApplication extends DefaultApplication {
  ApplicationType: 'Consumer',
  AppInfo: ConsumerFinanceAppInfo,
  ApplicantLocation: {lat: string, long: string},
  AssetSpec: AssetSpec,
  AuthorisedSignatory: null,
  CompanyDetails: null,
  Contacts: null,
  EditLockByUserId: number,
  Expense?: {
    CarLoans?: number,
    CreditCardLimit?: number,
    Education?: number,
    Electricity?: number,
    Groceries?: number,
    HomeLoans?: number,
    Insurance?: number,
    OtherLoans?: number,
    OtherUtilities?: number,
    PersonalLoans?: number,
    ShareofExpense?: number,
  },
  IsEditLock: boolean,
  Policies: null,
  PricingDetails: {
    AdverseOnFile: 'Yes' | 'No',
    AssetCondition: 'New' | 'Used',
    TreasuryAssetCondition?: 'New' | 'Used', // derived from asset age (<= 2yrs => New); synced to Treasury/SF
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    BrokerOriginationFee: number,
    Brokerage: number,
    Deposit: number,
    DocFee: number,
    DocFeeFinanced: string,
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    InterestRate: number,
    InvoiceAmount: number,
    AmountFinance: number,
    LoanAmount: number,
    LoanTerm: number,
    PaymentPeriod: PaymentPeriodType | null,
    PrivateSale: PrivateSaleType,
    PropertyOwner: 'Yes' | 'No',
    Repayment: number,
    Margin: number;
    Rate: number,
    PaymentType: string,
    APR: number,
    BrokerageAmount: number, // from SF
    BrokerOriginationFeeSf: number, // from SF
    DocFeeSf: number, // from SF
    RateSf: number, // from SF
    SpecialConditions?: string, // from SF,
    TransactionType?: TransactionType,
    PreviousLoan: 'Yes' | 'No',
    Hybrid: 'Yes' | 'No',
    CreditRateAdjustment?: number,
    RateDiscount?: number,
    OversCommission?: number,
    RepaymentType?: string,
    MonthlyAccountKeepingFee?: number,
  },
}

export interface InsurancePremiumApplication extends DefaultApplication {
  ApplicationType: 'InsurancePremium',
  AppInfo: InsurancePremiumAppInfo,
  ApplicantLocation: {lat: string, long: string},
  AssetSpec: null,
  AuthorisedSignatory: {
    Email: string,
    GivenName: string,
    MiddleName: string,
    MobileNumber: string,
    Role: string,
    SurName: string,
    Title: string,
  }[],
  CompanyDetails: null,
  Contacts: Contact,
  EditLockByUserId: null,
  Expense: null,
  IsEditLock: boolean,
  Policies?: {
    expiryDate: string,
    inceptionDate: string,
    insurer: string,
    invoiceNumber: string,
    policyNumber: string,
    premiumAmount: string,
    typeOfCover: string,
  }[],
  PricingDetails: {
    BrokerageAmount: number,
    DocFee: number,
    DocFeeFinanced: string,
    LoanTerm: number,
    PaymentPeriod: PaymentPeriodType | null,
    Margin: number;
    Rate: number,
    Repayment: string,
    TotalAmount: number,
    Deposit: number,
    DocFeeSf: number, // from SF
    RateSf: number, // from SF
    SpecialConditions?: string // from SF,
  },
}

export interface BusinessOverdraftApplication extends DefaultApplication {
  ApplicationType: 'BusinessOverdraft',
  LvrCalculatorValue: LvrCalculatorValue,
  DscrCalculatorValue: DscrCalculatorValue,
  AppInfo: BusinessOverdraftAppInfo,
  ApplicantLocation: {lat: string, long: string},
  AssetSpec: null,
  AuthorisedSignatory: null,
  CompanyDetails: SearchCompanyByABNResult,
  Contacts: Contact,
  EditLockByUserId: number,
  Expense: null,
  IsEditLock: false,
  Policies: null,
  PricingDetails: {
    FacilityEstablishmentFee: number,
    FacilityEstablishmentFeePercent: number,
    AdverseOnFile: 'Yes' | 'No',
    BankStatementSubmitted: string,
    BrokerOriginationFee: number,
    Brokerage: number,
    DocFee: number,
    DocFeeFinanced: string,
    EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
    InvoiceAmount: number,
    LoanAmount: number,
    AmountFinance?: number,  // filled by SF pricing details callback
    LoanTerm: number,
    RbaRate: number,
    PaymentPeriod: PaymentPeriodType | null,
    PropertyOwner: 'Yes' | 'No',
    Rate: number,
    CreditRateAdjustment?: number,
    Repayment: number,
    BalloonPayment: number,
    BalloonPaymentPercentage: number,
    DirectorScoreRate: 'Yes' | 'No',
    PaymentType: string,
    BrokerageAmount: number,
    BrokerOriginationFeeSf: number, // from SF
    DocFeeSf: number, // from SF
    RateSf: number, // from SF
    FacilityEstablishmentFeeSf: number, // from SF
    FacilityEstablishmentFeePercentSf: number, // from SF
    SpecialConditions?: string // from SF,
    PreviousLoan: 'Yes' | 'No',
    TermRenewal?: 'Yes' | 'No',
    EarlyTerminationFee?: number | null,
    Margin: number,
    MonthlyFacilityFeePercentage?: number,
    AdditionalCommission?: 'Yes' | 'No' | null,
    LastUpdatedByUser?: {id: number, name: string} | null,
    BrokerageManualOverride?: 'Yes' | 'No' | null,
  },
  PreviousBusiness: BusinessNumberSearchValue;
}


export type InteflowStatusStageType = null | 'Application Received' | 'Closed Lost' | 'Closed Won' | 'Credit Approved' |
  'Docs Out' | 'Documentation' | 'Lead' | 'More Information' | 'Proposal' | 'Prospect' | 'QA' | 'Settlement' | 'Settlement Pending' |
  'Submitted to Credit' | 'Waiting for Bank Statements';

export type Application = AssetFinanceApplication | InsurancePremiumApplication | BusinessLoansApplication | ConsumerFinanceApplication
 | TradeFinanceApplication | CorporateLoanApplication | CommercialFinanceApplication | BusinessOverdraftApplication;

// NOTE: we should not do this, applicationBackground is not FloorplanFinanceApplication
export interface ApplicationsReport {
  underReview: number,
  settlement: number,
  won: number,
}

export type ApplicationSort = { prop: 'AppInfoStageName'| 'BrokerName' | 'BrokerAppId' | 'Status' | 'CreateTime' | 'CompanyName', dir: 'ASC' | 'DESC'}[]
export interface ApplicationTypeFilter {
  type: AllApplicationTypes,
  name: string;
}

export type ApplicationState = 'underReview' | 'in_settlement' | 'closedWon';

export type SearchCompanyResult = SearchCompanyByNameResult | SearchCompanyByABNResult;

export type SearchCompanyByNameResult = {
  numberOfRecords: number;
  records: { organisationName: string, abn: number, status: 'Active' | 'Cancelled' }[]
  ABRPayloadSearchResults?: {
    response?: {
      exception?: {
        exceptionCode: string,
        exceptionDescription: string,
      }
    }
  },
};

export type SearchCompanyByABNResult = {
  ABN: {
    identifierValue: number,
    isCurrentIndicator: string,
    replacedFrom: string,
  }[] | {
    identifierValue: number,
    isCurrentIndicator: string,
    replacedFrom: string,
  },
  ABNAgeMonths: number,
  GSTAgeMonths: number,
  ABNAgeMonthsWithContinuity?: number,
  GSTAgeMonthsWithContinuity?: number,
  ASICNumber: number,
  entityStatus: {
    effectiveFrom: string, effectiveTo: string, entityStatusCode: 'Active' | 'Cancelled'
  }[] | {effectiveFrom: string, effectiveTo: string, entityStatusCode: 'Active' | 'Cancelled'},
  entityType: { entityDescription: string, entityTypeCode: string },
  goodsAndServicesTax: {
    effectiveFrom: string,
    efectiveTo: string,
  }[] | {effectiveFrom: string, effectiveTo: string},
  legalName: (
    {effectiveFrom: string, effectiveTo: string, familyName: string, givenName: string, otherGivenName: string}[] |
    {effectiveFrom: string, effectiveTo: string, familyName: string, givenName: string, otherGivenName: string})
  ,
  mainBusinessPhysicalAddress: {
    effectiveFrom: string,
    effectiveTo: string,
    postcode: number,
    stateCode: string,
  }[],
  mainTradingName: {
    organisationName: string,
    effectiveFrom: string,
    effectiveTo: string,
    orgnisationName: string,
  } | {
    organisationName: string,
    effectiveFrom: string,
    effectiveTo: string,
    orgnisationName: string,
  }[],
  organisationName: string,
  recordLastUpdatedDate: string,
  records: {organisationName: string, abn: number}[],
  ABRPayloadSearchResults?: {
    response?: {
      exception?: {
        exceptionCode: string,
        exceptionDescription: string,
      }
    }
  },
};


export type GeoLocation = {
  lat?: string, long?: string,
}


export interface BrokerOfUser {
  // AggregatorId: string,
  EntityName: string,
  SalesforceId: string,
  Relationship: 'disclosed' | 'undisclosed',
  ABN: string,
  ACN?: string,
  Type?: 'broker' | 'vendor' | 'dealer' | 'aggregator',
}

export interface SaveApplicationResult {
  ApplicationId: number;
  BrokerApplicationId: string;
}

export interface SubmitApplicationResult {
  CustomerId: string;     // salesforce Accountid
  ContactId: string;      // salesforce Contactid
  SalesforceId: string;   // salesforce Oppid
  StageName: string;      // salesforce StageName
  Status: string;         // salesforce Decision
  // Outcome of auto-generating the privacy consent forms on submission. Present when the
  // submit flow attempted consent generation; `failed > 0` means one or more signatories
  // need their contact details corrected ("Consent Dispatch Failed - Action Required").
  consentDispatch?: ConsentDispatchSummary;
}

export type ApplicationSource = 'portal' | 'external' | 'direct';


export interface SyncOpportunityToSFData {
  Deposit_paid_to__c: 'Dynamoney' | 'Supplier'
  Deposit_to_Dyna__c: number,
  Deposit_to_Supplier__c: number,
  Broker_Origination_Fee__c: number,
  Invoice_Price__c: number,
  Amount: number,
  Amount_Financed__c: number,
  Deposit_where_applicable__c: number,
  Doc_Fee_Financed__c: boolean,
  APR__c: number,
  Rate__c: number,
  Margin__c: number,
  Asset_Category__c: string, // 130, 137, 132, 133, 134, 135, 136, 106, Other, 139, 138, 140, 141, 142,
  Asset_Type__c: string, // eg. 130-1
  Make__c: string,
  Asset_Year__c: string,
  Goods_Description__c: string, // make family year model
  Loan_Purpose__c: string,
  Private_Sale__c: PrivateSaleType, // 'Yes', 'No', 'Sales and Lease Back', 'Capital Raise', 'Refinance', 'Balloon Refinance',
  Property_Ownership__c: 'Yes' | 'No', // 'Yes', 'No'
  Adverse__c: 'Yes' | 'No', // 'Yes', 'No'
  ec1_Term__c: number,  // number of months
  FacilityEstablishmentFee__c: number, // amount
  Facility_Establishment_Fee__c: number, // percent
  Brokerage__c: number,
  Brokerage_Percentage__c: number,
  New_or_Used__c: 'New' | 'Used' | 'NA'  // 'New', 'Used', NA
  Treasury_Asset_Condition__c?: 'New' | 'Used', // derived from asset age (<= 2yrs => New); portal-owned, outbound only
  Private_Sales__c: string, // 'Yes', 'No', 'Sales and Lease Back', 'Capital Raise', 'Refinance', 'Balloon Refinance'
  Repayment_Frequency__c: string, // 'Monthly', 'Weekly', 'Quarterly', 'Fortnightly'
  ProductType__c: string, // 'Business Loan', 'Chattel Mortgage', 'Finance Lease', 'Insurance Premium', 'Invoice Finance', 'Operating Lease', 'Rental', 'Software Agreement', 'Trade Finance', 'Asset Finance', 'Business Overdraft', 'Corporate Loan', 'Floorplan Finance'
  Product__c?: string;
  Documentation_Fee__c: number, // currency
  ec1_ResidualValue__c: number, // percent
  Residual_Value__c: number, // currency
  Overs_Commission__c: number,
  Exposure_At_Submission__c?: number, // Exposure at submission
  ContractVersion__c?: number, // contract iteration; normally set by syncOverdraftDetailsToSf
}

export interface SaveApplicationPricingDetailsData {
  LoanTerm?: number,
  InvoiceAmount?: number,
  Deposit?: number,
  Brokerage?: number,
  LoanAmount?: number,
  PaymentPeriod?: string,
  DocFee?: number,
  DocFeeFinanced?: 'Yes' | 'No',
  BrokerOriginationFee?: number,
  PropertyOwner?: 'Yes' | 'No',
  AdverseOnFile?: 'Yes' | 'No',
  EquifaxDirectorScoreThreshold?: EquifaxDirectorScoreThresholdValue,
  // AZ-1651: percent taken off this Asset Finance quote. 0 when the discount did not apply.
  PremiumDiscount?: number | null,
  Rate?: number,
  BalloonPaymentPercentage?: number,
  BalloonPayment?: number,
  Repayment?: number,
  BankStatementSubmitted?: 'Yes' | 'No',
  PrivateSale?: PrivateSaleType,
  AssetCondition?: string,
  TreasuryAssetCondition?: 'New' | 'Used', // derived from asset age (<= 2yrs => New); synced to Treasury/SF
  TransactionType?: TransactionType,
  PreviousLoan?: 'Yes' | 'No',
  LimitRequest?: number,
  AmountFinance?: number,
  InterestRate?: number,
  APR?: number,
  DirectorScoreRate?: 'Yes' | 'No',
  RbaRate?: number,
  BrokerageDollar?: number,
  FacilityEstablishmentFeePercent?: number,
  FacilityEstablishmentFee?: number,
  Margin?: number,
  BrokerageAmount?: number,
  CreditRateAdjustment?: number,
  LastUpdatedByUser?: {id: number, name: string} | null,
  Hybrid?: 'Yes' | 'No',
  ltv?: LtvSelectionType | null,
  securityType?: SecurityTypeSelectionType | null,
  RateDiscount?: number | null,
  OversCommission?: number | null,
  SaleRentBack?: string | null,
  ResidualValue?: number | null,
  MonthlyFacilityFeePercentage?: number | null,
  RepaymentType?: string | null,
  MonthlyAccountKeepingFee?: number | null,
  LoanPurpose?: LoanPurposeValue | null,
  TermRenewal?: 'Yes' | 'No',
  EarlyTerminationFee?: number | null,
  EarlyTerminationFeePercent?: number | null,
  AbnAgeAboveThresholdOverride?: 'Yes' | 'No' | null,
  BrokerageManualOverride?: 'Yes' | 'No' | null,
  AdditionalCommission?: 'Yes' | 'No' | null,
}
export interface SaveApplicationData {
  ApplicationType: ApplicationTypes;
  IP: string,
  // KYB "Key People" selections, persisted in the same transaction as the parties save
  // (folded in from the former standalone /save-kyb-key-people endpoint).
  kybKeyPeople?: KybKeyPeopleValue,
  /**
   * Individuals and commercial entities in the API shape (AZ-1475) - the only way a save carries
   * parties. Full-sync: an absent array leaves those parties untouched.
   *
   * Not an Application column, so the submit path strips it before the row is written.
   */
  parties?: ApplicationPartiesWrite,
  LvrCalculatorValue?: LvrCalculatorValue,
  DscrCalculatorValue?: DscrCalculatorValue,
  Source?: ApplicationSource,
  ApplicantLocation?: GeoLocation,
  UserId: number,
  AcceptQuotation?: boolean,
  ApplicationNotes?: string,
  // When true, the borrowing (primary) entity is a property owner and is added as a guarantor on
  // the generated contract. Contract-only in effect; does not affect credit / pricing.
  isBorrowerPropertyOwner?: boolean | null,
  FrankieOneCompleted?: boolean | null,
  DocusignPrivacyCompleted?: boolean | null,
  CompanyDetails?: SearchCompanyByABNResult,
  AdditionalBrokerCorrespondent?: UserSelectionValue,
  BrokerContact?: SalesforceContactSameAccountSelectionValue,
  OriginatorBusinessId?: number | null,
  Status?: string,
  DocumentTypes?: DocumentTag[],
  OppStatus?: InteflowStatusRecord,
  PreviousBusiness?: BusinessNumberSearchValue,
  AppInfo: {
    BrokerAppID?: string,
    IntroducerGroup?: string,
    Disclosed?: boolean,
    FinanceType?: string,
    UserId?: number,
    OriginatorBusinessId?: number | null,
    UserEmail?: string,
    UserFirstName?: string,
    UserLastName?: string,
    Notes?: string,
    BusinessUse?: 'Yes' | 'No',
    SelfServiceablilty?: 'Yes' | 'No',
    PrivacyConfirmation?: 'Yes' | 'No',
    BusinessSell?: string | null,
    BusinessSellTerms?: string | null,
    BrokerflowDocumentID?: string,
    BrokerSalesforceID?: string,
    BrokerAbn?: string,
    BrokerEntityName?: string,
    AssetCategory?: string,
    ContactId?: string | null,
    CustomerId?: string | null,
    StageName?: string | null,
    SalesforceId?: string | null,
    MetApplicant?: string,
    isSwitchToEasyDoc?: boolean,
    productType?: AssetFinanceProductType,
    ApplicationType?: string,
    OwnerId?: string,
    LoanPurpose?: LoanPurposeValue | null,
  }
  AssetSpec?: {
    make?: string,
    family?: string,
    year?: string,
    vehicle?: string,
    description?: string,
    LVR?: number,
    [data: string]: any,
  },
  Policies?: {
    typeOfCover: string,
    policyNumber: string,
    insurer: string,
    inceptionDate: string,
    expiryDate: string,
    premiumAmount: number,
    invoiceNumber: string,
  }[],
  Reference?: {
    CompanyName?: string,
    Telephone?: string,
    ContactPerson?: string,
    Type?: string,
  }[],
  // Background?:Background[],
  PricingDetails: SaveApplicationPricingDetailsData,
  Contacts?: {
    Title?: string,
    GivenName?: string,
    SurName?: string,
    Email?: string,
    MiddleName?: string,
    Telephone?: string,
    Mobile?: string,
    AreaCode?: string,
  },
  AuthorisedSignatory?: {
    Role: string,
    Title: string,
    GivenName: string,
    SurName: string,
    MiddleName: string,
    Email: string,
    MobileNumber: string,
  }[],
  Expense?: {
    HomeLoans: number,
    CarLoans: number,
    PersonalLoans: number,
    OtherLoans: number,
    Electricity: number,
    OtherUtilities: number,
    Education: number,
    Groceries: number,
    Insurance: number,
    ShareofExpense: number,
  },
  isDeleted?: boolean,
  SecurityPin?: string,
  SystemCreditStatus?: SystemCreditStatus,
  CreditOfficerStatus?: CreditOfficerStatus,
  creditOfficerId?: number,
  AssociatedEntities?: AssociatedEntity[],
}

export interface riskRating {
  growRiskRating: number,
  abnAge: number,
  gstAge: number,
  adverse: string,
  propertyOwnership: string,
  exceptionToPolicy: string,
  companyEquifaxScore: number,
  lowestIndividualEquifaxScore: number,
  highestIndividualEquifaxScore: number,
  lossReasonCategory: string,
  lossReasonDetails: string,
  creditDecisionFlag: string,
  closedLostDatetime: string,
  creditorWatchScore: number
}

/**
 * What the Salesforce Account/Contact/Opportunity mappers describe: an application as it was read
 * back, not as it was posted. cron-submit-application loads it with getApplicationById, so `parties`
 * here is the read payload the hydrator attaches rather than SaveApplicationData's narrower write
 * shape - the mappers never see a submit payload.
 *
 * Built off SaveApplicationData rather than Application because the mappers read AppInfo and
 * PricingDetails as one loose bag of fields; Application types them as a union per product, which
 * these reads cannot narrow.
 */
export type SalesforceSyncApplication = Omit<SaveApplicationData, 'parties'> & {
  parties?: GetApplicationPartiesPayload | null;
};

export interface UpdateApplicationData extends SaveApplicationData {
  Documents?: {
    new? : Base64File[],
    remove?: {
      // base64?: string,
      name: string,
    }[],
  },
  UpdateApplicationIgnoreStatus?: boolean,
}

/** What a submission wizard builds and posts. */
export type WizardSubmitData = UpdateApplicationData;


export type FetchABRdataByABNResult =  {
  acn?: string,
  abn?: string,
  entityStatus?: string,
  ABNAgeMonths?: number,
  yearsInBusiness?: number,
  GSTAgeMonths?: number,
  entityType?: 'PTY' | 'TRST' | 'PTNR' | 'PTY' | 'SLTR',
  organisationType?: string,
  legalName?: string;
};

export interface ApplicationStatistics {
  last30days: number;
  prevLast30days: number;
  prevMonth: number;
  prevMonthText: string;
  thisMonth: number;
  thisMonthText: string;
  totalApplications: number;
}

export interface OverdraftProductLast12MonthChartStatistic{
  year:number,
  month:number
  month_name:string,
  value:number
}
export interface OverdraftProductApplicationStatistics {
  totalBalance : number;
  totalLimit : number;
  totalActiveAccounts : number;
  totalActiveCards : number;
  totalBalanceArrears : number;
  balanceArrearsSum:number;
  numberAccountsArrears : number;
  get30DaysCardTransactionVolume:number,
  get30DayBankTransactionVolume:number,
  get60DaysCardTransactionVolume:number,
  get60DayBankTransactionVolume:number,
  get90DaysCardTransactionVolume:number,
  get90DayBankTransactionVolume:number,
  totalSpendLast30Days:number,
  totalSpendPrior30Days:number,
  totalSpend90Days:number,
  last30DaysNewAccountCount:number,
  prior30DaysNewAccountCount:number,
  currentMonthNewAccountCount:number,
  lastMonthNewAccountCount:number,
  last30DaysCardActiveCount:number,
  prior30DaysCardActiveCount:number,
  currentMonthCardActiveCount:number,
  lastMonthCardActiveCount:number,
  last12MonthNewAccountActiveCount:OverdraftProductLast12MonthChartStatistic [],
  last12MonthTotalLimit:OverdraftProductLast12MonthChartStatistic [],
  last12MonthSpentAmount:OverdraftProductLast12MonthChartStatistic [],
  last12MonthTotalBalance:OverdraftProductLast12MonthChartStatistic []
}

export interface ApplicationMonthlyStatistics {
  app_volume: { monthNumber: number, yearNumber: number, counts: number }[];
  monthly_settlement: { monthNumber: number, yearNumber: number, counts: number }[];
}

export interface Article {
  title: string;
  description: string;
  content: string;
  url: string;
  image: string;
  publishedAt: string;
  source: { url: string; name: string; };
}

export interface GetNewsResult {
  articles: Article[];
  status: string;
  totalArticles: number;
}

export interface InProgressApplicationStatistics {
  approved_apps: number;
  total_inprogress: number;
  underreview_apps: number;
}

export interface SettledApplicationStatistics {
  approved_apps: number;
  approved_last30days: number;
  approved_lastMonth: number;
  approved_lastprev30days: number;
  approved_thisMonth: number;
  prevMonthText: string;
  settled_apps: number;
  settled_last30days: number;
  settled_lastMonth: number;
  settled_lastprev30days: number;
  settled_thisMonth: number;
  submitted_apps: number;
  submitted_last30days: number;
  submitted_lastMonth: number;
  submitted_lastprev30days: number;
  submitted_thisMonth: number;
  thisMonthText: string;
  total_applications: number;
}

export interface CompanyDocumentData {
  legalname: string;
  abn: string;
  latestApplicationId: number;
  docs: AzureStorageDocument[];
}

export interface ConsumerDocumentData {
  consumername: string;
  dob: string;
  latestApplicationId: number;
  docs: AzureStorageDocument[];
}

export interface ApplicationDocumentsData {
  companyDocs: CompanyDocumentData[];
  consumerDocs: ConsumerDocumentData[];
}

export interface ApplicationDocumentsTableData {
  type: 'company' | 'consumer';
  companyLegalName: string;
  companyAbn: string;
  consumerName: string;
  consumerDob: string;
  latestApplicationId: number;
  docs: AzureStorageDocument[];
}

export interface DocumentCognitiveSearchResult {
  legalname: string | null;
  abn: string | null;
  consumername: string | null;
  dob: string | null;
  name: string | null;
  path: string | null;
  isdeleted: string | null;
}


export interface LastRecentApplicationsBundle {
  all: {
    total: number,
    records: SimplifiedApplication[],
  }
}

export interface SyncPricingDetailsBody extends InteflowStatusRecord {
  Id: string, // SalesforceId
  RecordTypeId: string,
  Record_Type_Name_Text__c: 'Accreditation' | 'Asset Finance' | 'Business Loan' | 'Business Overdraft' | 'Consumer Asset Finance' | 'Corporate Loan' | 'Insurance Premium' | 'Invoice Finance' | 'Trade Finance' ,
  FacilityEstablishmentFee__c?: number,
  Facility_Establishment_Fee__c?: number,
  // The one pricing field the portal never writes, so Salesforce is its source of truth.
  DS_Monthly_Facility_Fee__c?: number,
  // Written out by the portal; only arrives inbound once PortalUpdate.cls is updated to send it.
  MonthlyFacilityFeePercentage__c?: number,
  Residual_Value__c?: number,
  ec1_ResidualValue__c?: number,
  Broker_Origination_Fee__c?: number,
  Brokerage_ex_Origination__c?: number,
  Deposit_where_applicable__c?: number,
  Documentation_Fee__c?: number,
  Amount?: number,
  Invoice_Price__c?: number,
  Amount_Financed__c?: number,
  Loan_Amount__c?: number,
  ec1_Term__c?: number,
  Repayment_Frequency__c?: number,
  Rate__c?: number,
  Calculated_Repayment__c?: number,
  Calculated_Doc_Fee__c?: number,
  Doc_Fee_Financed__c?: string,
  Payment_Type__c?: string,
  Private_Sale__c?: string,
  Name?: string,
  ACN_Number__c?: string,
  ABN_Number__c?: string,
  Address__c?: string,
  StageName?: string,
  Special_Conditions__c?: string;
  Car_Loans__c?: number,
  Total_Credit_Card_Limits__c?: number;
  Education__c?: number;
  Electricity__c?: number;
  Groceries__c?: number;
  Home_Loans__c?: number;
  Insurance__c?: number;
  Other_Loans__c?: number;
  Other_Uttilities__c?: number;
  Personal_Loans__c?: number;
  Share_of_Monthly_Living_Expenses__c?: number;
  Repayment__c?: number;
  Additional_Security__c?: string;
  Goods_Description__c?: string;
  New_or_Used__c?: string;
  Margin__c?: number;
  Credit_Officer__c?: string;
  Product__c?: AssetFinanceProductType | string;
  OwnerId?: string;
  client_officer_most_recent__c?: string;
  Loan_Purpose__c?: string;
}

export interface ApplicationOwner {
  Id: string; // sf id
  Name: string;
  CompanyName: string | null;
  UserName: string;
  Email: string;
  MobilePhone: string | null;
  Address: string | null;
}

export interface ApplicationCreditOfficer {
  GivenName: string;
  FamilyName: string;
  Email: string;
  sfId:string;
  UserId: number;
}

export type GetApplicationOwnerFn = (salesforceId: string) => Observable<ApplicationOwner | null>;
export type GetApplicationCreditOfficerFn = (salesforceId: string) => Observable<ApplicationCreditOfficer | null>;
export type RefreshBankStatementFn = (abn: string) => Observable<PayloadApiResponse<Response | {Error: string}>>;

export type TaxInvoiceDetails = TaxInvoiceDetailsSuccess | TaxInvoiceDetailsError;
export interface TaxInvoiceDetailsSuccess {
  InvoiceId: string;
  InvoiceDate: string;
  AccountName: string;
  AccountNumber: string;
  Bsb: string;
  FinancialInstitution: string;
  Amount: string;
}
export interface TaxInvoiceDetailsError { Error: string }

export type ExtractTaxInvoiceFn = (file: File) => Promise<Observable<TaxInvoiceDetails>>;

export interface TaxInvoiceDetailsDialogData {
  taxInvoiceDetails: TaxInvoiceDetailsSuccess
}

export interface TaxInvoiceDetailsDialogResult {
  readyForSubmission: boolean;
  invoiceDetails: {
    invoiceId: string;
    invoiceDate: string;
    accountName: string;
    bsb: BsbComponentValue;
    accountNumber: string;
    financialInstitution: string;
    amount: CurrencyInputValue;
  }
}

export type UpdateApplicationFn = (ApplicationId: number, data: UpdateApplicationData) => Observable<ApiResponse>;

export type UpdateApplicationSfFn = (ApplicationId: number, data: UpdateApplicationData) => Observable<ApiResponse>;

export type GetOriginatorBusinessByIdFn = (originatorBusinessId: number) => Observable<OriginatorBusiness | null>;

export interface PrivacyConsent {
  id: number,
  type: 'application',
  individualId: string,
  status: PrivacyConsentStatus,
  applicationId: number,
  userId: number,
  createdTime: string,
  lastUpdatedTime: string,
  ip: string,
  FamilyName?: string,
  GivenName?: string,
  uuid?: string | null,
  // New schema (5.8.0): the normalised ApplicationIndividual.id this consent belongs to.
  applicationIndividualId?: number | null,
  // Auto-generated consent form flow (null formType = legacy manual consent record).
  formType?: PrivacyConsentFormType | null,
  dispatchFailed?: boolean,
  failureReason?: string | null,
  dispatchedViaEmail?: boolean,
  dispatchedViaSms?: boolean,
  // DocuSign envelope backing this form; used to void the signing link when the form is superseded
  // or invalidated before signing.
  docusignEnvelopeId?: string | null,
  // Server-side link deactivation: false once the form has been signed/superseded.
  linkActive?: boolean,
  // JSON snapshot of the values the consent form was pre-populated with at generation time.
  formData?: string | null,
  // Timestamp of the latest status change (Story 2 consolidated view).
  statusChangedTime?: string | null,
  // Signed-document attachment (Story 1). signedTime is set when the DocuSign envelope
  // completes; the signature event is persisted here before the Azure upload is attempted.
  signedTime?: string | null,
  documentAttached?: boolean,
  documentBlobPath?: string | null,
  documentAttachmentAttempts?: number,
  documentAttachmentError?: string | null,
  // True once the signed-document attachment auto-retries are exhausted (dead-lettered).
  documentAttachmentExhausted?: boolean,
  // Dispatch auto-retry bookkeeping. dispatchRetryable distinguishes transient failures
  // (network / DocuSign 5xx, auto-retried) from permanent data failures (Action Required).
  dispatchAttempts?: number,
  dispatchRetryable?: boolean,
  // Set only on an accepted row an admin completed manually in place of a customer signature.
  // The mandatory reason doubles as the marker: null means the customer signed in DocuSign.
  bypassReason?: string | null,
}

// Distinguishes the auto-generated, pre-populated consent form from legacy manual consent records.
export type PrivacyConsentFormType = 'product-application-consent';

export const PRODUCT_APPLICATION_CONSENT_FORM_TYPE: PrivacyConsentFormType = 'product-application-consent';

// Portal-facing labels for the auto-generated consent form (see the consent-form user stories).
// The consolidated status view (Story 2) uses the short vocabulary: Pending - Customer Signature,
// Completed, Superseded, Expired, Dispatch Failed. CONSENT_DISPATCH_FAILED_LABEL keeps the longer
// "Action Required" wording for the broker-facing failure banner in the KYC tab.
export const CONSENT_PENDING_SIGNATURE_LABEL = 'Pending \u2013 Customer Signature';
export const CONSENT_SIGNED_LABEL = 'Signed';
export const CONSENT_COMPLETED_LABEL = 'Completed';
export const CONSENT_SUPERSEDED_LABEL = 'Superseded';
export const CONSENT_INVALIDATED_LABEL = 'Invalidated';
export const CONSENT_EXPIRED_LABEL = 'Expired';
// 'expired' is used for two distinct cases, disambiguated by signedTime: a signature that has aged
// past CONSENT_MAX_AGE_DAYS (signedTime set - the customer must re-sign) versus a DocuSign envelope
// that expired/voided before it was ever signed (signedTime null).
export const CONSENT_EXPIRED_SIGNATURE_LABEL = 'Expired \u2013 Re-sign Required';
export const CONSENT_EXPIRED_UNSIGNED_LABEL = 'Expired \u2013 Unsigned';
export const CONSENT_DECLINED_LABEL = 'Declined by Customer';
// An accepted record an admin completed manually; distinguished from a customer signature so the
// KYC tab never implies the customer signed a form they were never sent.
export const CONSENT_MANUALLY_COMPLETED_LABEL = 'Manually Completed';
export const CONSENT_DISPATCH_FAILED_SHORT_LABEL = 'Dispatch Failed';
export const CONSENT_DISPATCH_FAILED_LABEL = 'Consent Dispatch Failed \u2013 Action Required';

// Values the consent form DOCX template (ProductApplicationConsentForm.docx) is pre-populated with.
// Keys map 1:1 to the +++INS <key>+++ placeholders in the template.
export interface ConsentFormPrefillData {
  applicationId: string;
  dateSubmitted: string;
  product: string;
  loanPurpose: string;
  loanAmount: string;
  loanTerm: string;
  companyName: string;
  companyType: string;
  annualRevenue: string;
  abn: string;
  acn: string;
  principalPlaceOfBusiness: string;
  businessPhone: string;
  businessEmail: string;
  roleApplicant: string;
  roleGuarantor: string;
  roleDirector: string;
  roleAuthorisedSignatory: string;
  rolePartner: string;
  roleTrustee: string;
  fullName: string;
  dateOfBirth: string;
  aliasName: string;
  nationality: string;
  drivingLicenceNo: string;
  passportNo: string;
  emailAddress: string;
  phoneNo: string;
  currentResidentialAddress: string;
}

// Per-signatory outcome of an auto generate-and-dispatch run (on submission / on add-individual).
export interface ConsentDispatchResultItem {
  applicationIndividualId: number | null;
  individualId: string | null;
  name: string;
  dispatched: boolean;
  dispatchFailed: boolean;
  failureReason: string | null;
  privacyConsentId?: number;
}

// Outcome of an admin manually completing a signatory's privacy consent. `bypassed` is false when
// the individual already held a valid consent, so the action was a no-op.
export interface PrivacyConsentBypassResult {
  applicationIndividualId: number | null;
  individualId: string | null;
  name: string;
  bypassed: boolean;
  privacyConsentId?: number;
}

export interface ConsentDispatchSummary {
  generated: number;
  failed: number;
  results: ConsentDispatchResultItem[];
}

// The officer's explicit selection on each save side effect offered as a checkbox in the
// confirmation dialog: true = run it (left ticked), false = skip it. An absent key means the
// side effect was not offered for that individual. The server honours skips for internal users
// only and writes every offered selection to the application audit log together with the outcome.
export interface SavePartySideEffectDecision {
  individualId: string;
  reverify?: boolean;
  consentReissue?: boolean;
}

// One changed field on a party save, with display-ready old/new values for the audit table's
// Old Value / New Value columns. Values are formatted client-side (it owns the form value shapes);
// the server stores them verbatim.
export interface SavePartyChangedField {
  field: string;
  oldValue: string;
  newValue: string;
}

// One dirty individual to persist in a consolidated "save parties" request. Entities will be added
// here later (the endpoint is named for parties = individuals + commercial entities).
export interface SaveApplicationPartyIndividualInput {
  individualId: string;
  data: UpdateIndividualData;
  /**
   * The kycIndividualChanges the officer edited, written as one audit row per field so the Old
   * Value / New Value columns show the diff. Client-reported context only - the server derives the
   * side-effect outcomes authoritatively and the data change itself is in the payload.
   */
  changedFields?: SavePartyChangedField[];
}

/**
 * One party to remove in a consolidated "save parties" request. `kind` selects the table; `id` is
 * the ApplicationCommercialEntity.id or ApplicationIndividual.id (numeric, not the legacy GUID).
 * `role` is the role being removed for an individual who may hold several (trustee/partner/
 * guarantor); omitting it removes every role the party holds.
 */
export interface RemoveApplicationPartyInput {
  kind: 'entity' | 'individual';
  id: number;
  role?: 'trustee' | 'partner' | 'guarantor';
}

// Body of POST /api2/save-application-parties: save all dirty individuals and entities in one
// request. The server re-verifies digital ID and invalidates/reissues privacy consent per affected
// individual. Individuals and entities are deliberately saved together: a company-block edit affects
// every signatory's form, so splitting them across two requests would reissue the same form twice.
export interface SaveApplicationPartiesData {
  applicationId: number;
  applicationType: ApplicationTypes;
  individuals: SaveApplicationPartyIndividualInput[];
  entities?: SaveApplicationPartyEntityInput[];
  /**
   * Parties to soft-delete (non-primary entities and individual trustees/partners/guarantors).
   * Explicit and scoped: only the parties named here are removed, never inferred from absence,
   * matching the keyPeople partial-view contract. Internal users only; blocked from Docs Out.
   */
  removedParties?: RemoveApplicationPartyInput[];
  /**
   * The officer's checkbox selections from the confirmation dialog, per individual - covering both
   * edited individuals and company-block sweep signatories. Drives the side-effect skips (internal
   * users only) and is written to the audit log verbatim.
   */
  sideEffectDecisions?: SavePartySideEffectDecision[];
  /**
   * Whether to issue or reissue the privacy consent required by newly selected KYB parties.
   * Internal officers may opt out in the save confirmation dialog; other users cannot suppress it.
   */
  issueConsentForSelectedParties?: boolean;
  /**
   * False when an internal officer unticked the credit-flow run the save confirmation announced for
   * an existing individual becoming a guarantor. The server re-derives whether the run applies from
   * the saved roles and consents, and ignores false from non-internal users.
   */
  runCreditFlowOnGuarantorChange?: boolean;
  /**
   * Select/guarantor/signer tick changes, by KYB stakeholder. Only the stakeholders named here are
   * touched: unlike the wizard's key-people save this is not a full sync, because the KYC/AML tab does
   * not render the whole key-people picture (it has no Add Partner / Add Trustee), so treating absence
   * as removal would delete parties the screen never showed.
   */
  keyPeople?: KybPersonRowStateWithDetails[];
  /** @deprecated Use kybReconciliations. Retained temporarily for existing clients. */
  kybReconciliation?: KybReconciliationSaveData;
  /** Staff reconciliation decisions for every touched, valid entity, processed in array order. */
  kybReconciliations?: KybReconciliationSaveData[];
}

export type KybRiskAcknowledgementAction = 'CREDIT_APPROVAL' | 'DOCUMENT_ISSUE' | 'SETTLEMENT';

export interface KybRiskStatus {
  requiresAcknowledgement: boolean;
  unresolvedEntityCount: number;
  pendingKybEntityCount: number;
  pendingReconciliationEntityCount: number;
}

export interface KybRiskAcknowledgementData {
  applicationId: number;
  action: KybRiskAcknowledgementAction;
}

/**
 * Full details for a KYB stakeholder being materialised as an application individual for the first
 * time, captured in the KYC/AML tab details dialog. Carried on the key-people save so the server
 * creates a complete ApplicationIndividual instead of a sparse name/DOB-only record. Optional:
 * entries without details keep the existing sparse behaviour.
 */
export interface KeyPersonNewIndividualDetails {
  title: string;
  givenName: string;
  middleName?: string;
  surName: string;
  dateOfBirth: string;
  email: string;
  mobileNumber: string;
  residentialAddress: {
    address?: string;
    unitNumber?: string;
    streetNumber?: string;
    streetName?: string;
    streetType?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
  };
  countryOfCitizenship?: string;
  hasAlias?: boolean;
  aliasNames?: string;
  identificationType?: string;
  identificationNumber?: string;
}

/** A key-people tick change plus, for a first-time-selected stakeholder, the details to materialise. */
export interface KybPersonRowStateWithDetails extends KybPersonRowState {
  details?: KeyPersonNewIndividualDetails;
}

// Per-individual outcome of a save-parties request (best-effort reverify + consent reissue).
export interface SaveApplicationPartyResultItem {
  individualId: string;
  name: string;
  saved: boolean;
  reverified: boolean;
  consentReissued: boolean;
  consentDispatchFailed: boolean;
  // True when the side effect would have run but the officer opted out (internal users only).
  reverifySkipped: boolean;
  consentReissueSkipped: boolean;
  message: string | null;
}

export interface SaveApplicationPartiesSummary {
  saved: number;
  consentReissued: number;
  /** Number of commercial entities persisted in this request. */
  entitiesSaved: number;
  /** Number of KYB stakeholders whose select/guarantor/signer ticks were persisted. */
  keyPeopleSaved: number;
  /** Backward-compatible flag: true when this request completed at least one reconciliation. */
  kybReconciliationSaved?: boolean;
  /** Number of entity reconciliations completed by this request. */
  kybReconciliationsSaved?: number;
  /** Per-reconciliation outcome; failures do not roll back earlier successful entities. */
  kybReconciliationResults?: KybReconciliationSaveResult[];
  /** Number of commercial entities soft-deleted via removedParties. */
  entitiesRemoved: number;
  /** Number of individuals soft-deleted (or role-removed) via removedParties. */
  individualsRemoved: number;
  results: SaveApplicationPartyResultItem[];
}

// created/opened = form dispatched, awaiting signature; accepted = signed/Completed;
// superseded = replaced by a newer form after a details change; expired = DocuSign envelope
// expired/voided before signing; declined = signer received the form and refused to sign
// (a terminal signer decision, NOT a dispatch failure); invalidated = an unsigned (created/opened)
// form voided because a critical field changed and a fresh form was reissued to replace it.
export type PrivacyConsentStatus = 'created' | 'opened' | 'accepted' | 'superseded' | 'expired' | 'declined' | 'invalidated';


// NOTE: used by SalesforceContactSameAccountSelectionValue (in contact-same-company-selection-component.domain.ts)
export interface BrokerContactType {
  FirstName: string,
  LastName: string,
  AccountId: string, // salesforce account id
  Email: string,
  MobilePhone: string,
  Id: string, // salesforce contact id
}

export interface EditInvoiceAmountDialogData {
  title: string;
  invoiceAmount: number;
  depositAmount: number;
  min: number;
  minDeposit: number | null;
  maxDeposit: number | null;
  originalLoanAmount: number;
  limitMaxInvoiceAmount: boolean;
  financeType: FinanceType;
  isPropetyOwner: boolean;
  isConsumer: boolean;
  assetCategoryIndex: string;
  isCreditOrAdmin: boolean;
  productType?: AssetFinanceProductType;
}

export interface EditInvoiceAmountDialogResult {
  readyForSubmission: boolean;
  invoiceAmount: number;
  depositAmount: number;
}

export interface BankStatementsUploadObject {
  applicationId: number
}

export interface SfContact {
  userId: number,
  sfContactId?: string,
  sfAccountId: string,
  firstname: string,
  lastname: string,
  middlename: string,
  email: string,
  mobile: string,
}

export interface SFOpportunityData {
  AccountId?: string;
  BrokerContact__c?: string;
  MonthlyFacilityFeePercentage__c?: number;
  Product__c?: string;
  Payment_Type__c?: string;
  isSwitchToEasyDoc__c?: boolean;
  RecordTypeId?: string;
  Sale_And_Rent_Back__c?: string;
  Private_Sale__c?: string;
  FacilityEstablishmentFee__c?: number;
  Facility_Establishment_Fee__c?: number;
  Brokerage__c?: number;
  Asset_Year__c?: string;
  ProductType__c?: string;
  Overs_Commission__c?: number;
  New_or_Used__c?: string;
  Treasury_Asset_Condition__c?: string; // derived from asset age (<= 2yrs => New); portal-owned, outbound only
  FrankieOne_Completed__c?: boolean; // portal-owned, outbound only
  Docusign_Privacy_Completed__c?: boolean; // portal-owned, outbound only
  ec1_ResidualValue__c?: number;
  Residual_Value__c?: number;
  Goods_Description__c?: string;
  Loan_Purpose__c?: string;
  LVR__c?: number;
  Asset_Category__c?: string;
  Asset_Type__c?: string;
  Name?: string;
  CloseDate?: string;
  StageName?: string;
  Amount?: number;
  ec1_Term__c?: number;
  Repayment_Frequency__c?: string;
  PropertyOwnership__c?: string;
  Inteflow_Reference_Id__c?: string;
  Doc_Fee_Financed__c?: boolean;
  Brokerage_Percentage__c?: number;
  Broker_Origination_Fee__c?: number;
  Documentation_Fee__c?: number;
  Deposit_where_applicable__c?: number;
  ec1_Introducer__c?: string;
  Portal_User_Email__c?: string;
  Portal_User_First_Name__c?: string;
  Portal_User_Last_Name__c?: string;
  Rate__c?: number;
  Margin__c?: number;
  Notes__c?: string;
  Type?: string;
  OwnerId?: string;
  APR__c?: number;
  Broker_Contact_Add__c?: string;
  Broker_Email_Add__c?: string;
  PortalLink__c?: string;
  Employer__c?: string;
  Employer_Contact__c?: string;
  Employment_Status__c?: string;
  Applicant_Income_after_tax__c?: number;
  Previous_Employer__c?: string;
  Previous_Employer_Contact__c?: string;
  Time_at_address_in_months__c?: number;
  Number_of_Dependents__c?: number;
  Previous_Address__c?: string;
  Spouse_Income_after_tax__c?: number;
  Spouse_Employer__c?: string;
  Spouse_Employer_Contact__c?: string;
  Spouse_Employment_Status__c?: string;
  Home_Loans__c?: number;
  Car_Loans__c?: number;
  Personal_Loans__c?: number;
  Total_Credit_Card_Limits__c?: number;
  Other_Loans__c?: number;
  Electricity__c?: number;
  Other_Uttilities__c?: number;
  Education__c?: number;
  Groceries__c?: number;
  Insurance__c?: number;
  Share_of_Monthly_Living_Expenses__c?: number;
  Make__c?: string;
}

export interface SFAccountData {
  RecordTypeId: string,
  ec1_ABN__c?: string,
  ec1_ACN__c?: string,
  AnnualRevenue?: number,
  ec1_ANZSICCode__c?: string,
  ec1_EntityType__c?: string,
  ec1_Industry__c?: string,
  Interflow_Entity_Type__c?: string,
  ec1_TradingName__c?: string,
  Website?: string,
  Type?: string,
  Name: string,
  ec1_Email__c: string,
  BillingStreet: string,
  BillingCity: string,
  BillingState: string,
  BillingPostalCode: string,
  Phone: string,
  Business_Loans__c?: boolean,
  Trade_Finance__c?: boolean,
  Insurance_Premium_Finance__c?: boolean,
  Asset_Finance__c?: boolean,
  OwnerId?: string,
  PrimaryContact__c?: string;
  ec1_MobileNumber__c?: string;
  Aggregator__c?: string;
  ec1_Accredited__c?: boolean;
}

export interface SFContactArrayData {
  RecordTypeId: string,
  FirstName: string[],
  LastName: string[],
  Salutation: string[],
  Email: string[],
  MobilePhone: string[],
  Phone: string[],
  ec1_DateofBirth__c: string[],
  MailingStreet: string[],
  MailingCity: string[],
  MailingState: string[],
  MailingPostalCode: string[],
  ec1_Guarantor__c: boolean[],
  Not_a_contract_signer__c: boolean[],
}
export interface SFUserData {
  Username: string;
  Email: string;
  Address:string;
  Id?: string;
  MobilePhone?:string;
}

export interface SFContactData {
  AccountId?: string,
  RecordTypeId: string,
  FirstName: string,
  LastName: string,
  Salutation: string,
  Email: string,
  MobilePhone: string,
  Phone: string,
  ec1_DateofBirth__c?: string,
  MailingStreet: string,
  MailingCity: string,
  MailingState: string,
  MailingPostalCode: string,
  ec1_Guarantor__c?: boolean,
  Not_a_contract_signer__c?: boolean,
  ec1_DriversLicence__c?: string,
  Expiry_Date__c?: Moment,
  State__c?: string,
}
export interface QuotationNotificationDialogData {

}

export interface QuotationNotificationDialogResult {
  readyForSubmission: boolean;
  emailList: string[];
}

export interface ConsumerQuotationTableData {
  id?: number;
  type: 'download' | 'email';
  quotation: string;
  fileId: string;
  emailList: string;
  userId: number;
  createdTime?: string;
}

export interface ConsumerQuotationData {
  monthlyPayment: number;
  monthlyInstallment: number;
  monthlyAccountKeepingFee: number;
  interestRate: number;
  comparisonRate: number;
  amountFinanced: number;
  totalInterest: number;
  totalPayment: number;
  invoiceAmount: number;
  deposit: number;
  residual: number;
  docFee: number;
  loanTerms: number;
  paymentFrequency: RepaymentFrequencyType;
}

export interface ConsumerQuotationTemplateData {
  monthlyPayment: string;
  monthlyInstallment: string;
  monthlyAccountKeepingFee: string;
  interestRate: string;
  comparisonRate: string;
  amountFinanced: string;
  totalInterest: string;
  totalPayment: string;
  invoiceAmount: string;
  deposit: string;
  residual: string;
  docFee: string;
  loanTerms: string;
  paymentFrequency: RepaymentFrequencyType;
}

export interface ConsumerQuotationTemplateDocData extends ConsumerQuotationTemplateData {
  generatedTime: string;
  ip: string;
  consumerFirstName: string;
  consumerLastName: string;
  fileId: string;
}

export interface SendConsumerQuotationData {
  data: ConsumerQuotationData;
  emailList: string[];
  consumerFirstName: string;
  consumerLastName: string;
}



export interface DownloadConsumerQuotationData {
  data: ConsumerQuotationData;
  ip: string;
  generatedTime: string;
  consumerFirstName: string;
  consumerLastName: string;
}

export interface WithdrawApplicationBody {
  salesforceId: string;
  applicationId: number;
  reason: string;
  comment: string;
}

export interface WithdrawApplicationDialogData {

}

export interface WithdrawApplicationDialogResult {
  readyForSubmission: boolean;
  reason: string;
  comment: string;
}

export const withdrawApplicationReasonOptions: string[] = [
  'Approval Time-Lapse',
  'Bankstatements request',
  'Credit SLA',
  'LTV Requirements',
  'Price Loading',
  'Pricing - Lost to Bank',
  'Pricing - Lost to Non-Bank',
  'Request For Information',
  'Valuation',
  'Lost contact with customer',
  'Error by Grow'
]

export interface ApplicationSettlementsOfficer {
  GivenName: string;
  FamilyName: string;
  Email: string;
  sfId:string;
  sfContactId?:string;
  UserId: number;
}

export interface UpdateSettlementsOfficerForApplication {
  applicationId: number;
  /** Null clears the officer. */
  settlmentUserId: number | null;
}

export interface UpdateAmlOfficerForApplication {
  applicationId: number;
  /** Null clears the officer. */
  amlOfficerUserId: number | null;
}

export type UpdateApplicationAmlOfficerFn = (body: UpdateAmlOfficerForApplication) => Observable<ApiResponse>;

export type WithdrawApplicationFn = (body: WithdrawApplicationBody) => Observable<ApiResponse>;

export type GetApplicationBySalesforceIdFn = (salesforceId: string) => Observable<Application | null>;

export type GetApplicationDscrFn = (applicationId: number) => Observable<number>;

export type GetActiveCreditUsersFn = () => Observable<UserOmitPriviledges[]>;

export type UpdateCreditAssignableFn = (userId: number, isAssignable: boolean) => Observable<ApiResponse>;

export type GetApplicationSettlementsOfficerFn = () => Observable<SearchableSelectItem[] | null>;

export type UpdateApplicationSettlementsOfficerFn = (body: UpdateSettlementsOfficerForApplication) => Observable<ApiResponse>;

export const creditDeclineCategoryOptions: string[] = [
  'Credit Declined',
  'Withdrawn'
];



export interface NewApplicationSubmissionRecord {
  applicationId: number;
  salesforceId: string;
  status: 'successful' | 'failed'
  error?: string;
  sequenceNumber: number;
}

export type ReportStatus = 'created' | 'completed' | 'pending' | 'failed';

export type ReportType =  
  'reporting-banking-transactions' |
  'reporting-brokerage-transactions' |
  'reporting-pismo-accounting-events' |
  'reporting-pismo-accounts-balances' |
  'reporting-pismo-accounts-limits' |
  'reporting-pismo-transactions-balance' |
  'reporting-pismo-transactions-denied' |
  'reporting-pismo-portfolio-balance' |
  'reporting-pismo-arrears' |
  'reporting-pismo-accounting-raw' |
  'reporting-pismo-accounting-aggregated' |
  'reporting-pismo-internal-accounts' | 
  'reporting-authorization-clearing' |
  'reporting-pismo-applepay';

export interface ReportRecord {
  id: number;
  userId: number;
  reportType: ReportType;
  startDate?: string;
  endDate?: string;
  status: ReportStatus;
  isDeleted: boolean;
  reportPath?: string;
  requestTime: Date;
  completedTime?: Date;
  lock: boolean;
  programId: number;
  userGivenName?: string;
  userFamilyName?: string;
}

export type ReportSortType = SortTypes<'reportType' | 'requestTime' | 'completedTime' | 'programId'>;

export interface TradeReportingEntry {
  type:
    'reporting-banking-transactions' |
    'reporting-brokerage-transactions' |
    'reporting-pismo-accounting-events' |
    'reporting-pismo-accounts-balances' |
    'reporting-pismo-accounts-limits' |
    'reporting-pismo-transactions-balance' |
    'reporting-pismo-transactions-denied' |
    'reporting-pismo-portfolio-balance' |
    'reporting-pismo-arrears' |
    'reporting-pismo-accounting-raw' |
    'reporting-pismo-accounting-aggregated' |
    'reporting-pismo-internal-accounts' | 
  'reporting-authorization-clearing' |
  'reporting-pismo-applepay',
  description: string,
}

export type RequestReportFn = (reportType: string, startDate: string, endDate: string, programId: number) => Observable<PayloadApiResponse<{ status: boolean }>>;

export interface NewReportDialogData {
  requestReportFn: RequestReportFn;
  getPismoProgramsFn: getPismoProgramsFn;
}

export interface NewReportDialogResult {

}

export interface ReportRequestDialogData {
  message?: string;
  mandatory: boolean;
  submitButtonText?: string;
  programSelectable: boolean;
  getPismoProgramsFn: getPismoProgramsFn;
}

export interface ReportRequestDialogResult {
  type: string,
  startDate: Moment  | null,
  endDate: Moment  | null,
  pismoProgram: PismoProgram | null,
}

// ANZSIC Update Types
export interface UpdateApplicationAnzsicBody {
  applicationId: number;
  industryDivision: IndustryDivision;
  industryGroup: IndustryGroup;
  industryClassDetail: IndustryClass;
}

export type UpdateApplicationAnzsicFn = (body: UpdateApplicationAnzsicBody) => Observable<ApiResponse>;

export interface UpdateApplicationBorrowerPropertyOwnerBody {
  applicationId: number;
  isBorrowerPropertyOwner: boolean;
}

export type UpdateApplicationBorrowerPropertyOwnerFn = (body: UpdateApplicationBorrowerPropertyOwnerBody) => Observable<ApiResponse>;

export interface EditAnzsicDialogData {
  application: Application;
  getIndustryDivisionsFn: GetIndustryDivisionsFn;
  getIndustryGroupsByDivisionCodeFn: GetIndustryGroupsByDivisionCodeFn;
  getIndustryClassesByGroupCodeFn: GetIndustryClassesByGroupCodeFn;
  updateApplicationAnzsicFn: UpdateApplicationAnzsicFn;
}

export interface EditAnzsicDialogResult {
  updated: boolean;
}

export interface EditBorrowerPropertyOwnerDialogData {
  application: Application;
  updateApplicationBorrowerPropertyOwnerFn: UpdateApplicationBorrowerPropertyOwnerFn;
}

export interface EditBorrowerPropertyOwnerDialogResult {
  updated: boolean;
}

// Loan Purpose Update Types
export interface UpdateLoanPurposeBody {
  applicationId: number;
  loanPurpose: LoanPurposeValue;
  termRenewal?: 'Yes' | 'No';
}

export type UpdateLoanPurposeFn = (body: UpdateLoanPurposeBody) => Observable<ApiResponse>;

export type CheckActiveOverdraftAccountFn = (abn: string) => Observable<PayloadApiResponse<CheckActiveOverdraftAccountDetails>>;

export interface EditLoanPurposeDialogData {
  application: Application;
  updateLoanPurposeFn: UpdateLoanPurposeFn;
  checkActiveOverdraftAccountFn: CheckActiveOverdraftAccountFn;
}

export interface EditLoanPurposeDialogResult {
  updated: boolean;
}